import axios from 'axios';

// Create an axios instance with default config
const API = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5001/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add a request interceptor to include the auth token in all requests
API.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Add a response interceptor to handle common errors
API.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      // Unauthorized - clear token and redirect to login
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (email: string, password: string) => 
    API.post('/users/login', { email, password }),
  getUserProfile: () => 
    API.get('/users/profile'),
  updateUserProfile: (userData: any) => 
    API.put('/users/profile', userData),
};

// Products API
export const productsAPI = {
  getProducts: (params = {}) => 
    API.get('/products', { params }),
  getProductById: (id: string) => 
    API.get(`/products/${id}`),
  getCategories: () =>
    API.get('/products/categories'),
  createProduct: (productData: FormData) => 
    API.post('/products', productData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }),
  updateProduct: (id: string, productData: FormData) => 
    API.put(`/products/${id}`, productData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }),
  deleteProduct: (id: string) => 
    API.delete(`/products/${id}`),
  deleteProductImage: (productId: string, imageId: string) => 
    API.delete(`/products/${productId}/images/${imageId}`),
  setMainProductImage: (productId: string, imageId: string) => 
    API.put(`/products/${productId}/images/${imageId}/main`),
  
  getProductsWithCatalogs: () => 
    API.get('/products/with-catalogs'),

  getProductCatalog: (productId: string) => 
    API.get(`/products/${productId}/catalog`),

  downloadProductCatalog: (productId: string) => 
    API.get(`/products/${productId}/catalog/download`, {
    responseType: 'blob'
  }),

uploadProductCatalog: (productId: string, formData: FormData) => 
  API.post(`/products/${productId}/catalog`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),

deleteProductCatalog: (productId: string) => 
  API.delete(`/products/${productId}/catalog`),
};

// Services API
export const servicesAPI = {
  getServices: (params = {}) => 
    API.get('/services', { params }),
  getServiceById: (id: string) => 
    API.get(`/services/${id}`),
  createService: (serviceData: FormData) => 
    API.post('/services', serviceData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }),
  updateService: (id: string, serviceData: FormData) => 
    API.put(`/services/${id}`, serviceData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }),
  deleteService: (id: string) => 
    API.delete(`/services/${id}`),
  deleteServiceImage: (serviceId: string, imageId: string) => 
    API.delete(`/services/${serviceId}/images/${imageId}`),
  setMainServiceImage: (serviceId: string, imageId: string) => 
    API.put(`/services/${serviceId}/images/${imageId}/main`),
};

// Partners API
export const partnersAPI = {
  getPartners: (params = {}) => 
    API.get('/partners', { params }),
  getPartnerById: (id: string) => 
    API.get(`/partners/${id}`),
  createPartner: (partnerData: FormData) => 
    API.post('/partners', partnerData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }),
  updatePartner: (id: string, partnerData: FormData) => 
    API.put(`/partners/${id}`, partnerData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }),
  deletePartner: (id: string) => 
    API.delete(`/partners/${id}`),
};

// Contacts API
export const contactsAPI = {
  getContacts: (params = {}) => 
    API.get('/contact', { params }),
  getContactById: (id: string) => 
    API.get(`/contact/${id}`),
  markContactAsRead: (id: string) => 
    API.put(`/contact/${id}/read`),
  deleteContact: (id: string) => 
    API.delete(`/contact/${id}`),
};

// Company Info API
export const companyInfoAPI = {
  getCompanyInfo: () => 
    API.get('/company-info'),
  updateCompanyInfo: (companyInfoData: any) => 
    API.put('/company-info', companyInfoData),
  uploadLogo: (logoFormData: FormData) => 
    API.post('/company-info/logo', logoFormData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }),
};

// Content Management API
export const contentAPI = {
  // Get content for a specific page
  getPageContent: (page: string, language: string = 'EN') => 
    API.get(`/content/${page}`, { params: { lang: language } }),
  
  // Update content for a specific page
  updatePageContent: (page: string, contentData: any, language: string = 'EN') => 
    API.put(`/content/${page}`, { data: contentData, language }),
    
  // Get all available content pages
  getAllContentPages: () => 
    API.get('/content'),
};

export default API; 